<script setup lang="ts">
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { unrefElement } from "@vueuse/core";
import { DialogController } from "./Dialog/dialog-controller";

const props = defineProps({
  side: {
    type: String as PropType<"top" | "bottom" | "left" | "right">,
    default: "bottom",
  },
  align: {
    type: String as PropType<"start" | "center" | "end">,
    default: "center",
  },
  controller: {
    type: Object as PropType<DialogController>,
    required: false,
  },
  triggerMethods: {
    type: Array as PropType<("hover" | "click")[]>,
    default: () => ["click"],
  },
  showTriggerMethods: {
    type: Array as PropType<("hover" | "click")[]>,
    required: false,
  },
  hideTriggerMethods: {
    type: Array as PropType<("hover" | "click")[]>,
    required: false,
  },
  triggerClass: {
    type: String,
    required: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  preventFocusOutside: {
    type: Boolean,
    default: false,
  },
  portalToClosest: {
    type: String,
    required: false,
  },
  contentClass: {
    type: String,
    required: false,
  },
  collisionPadding: {
    type: Number,
    default: 0,
  },
  sticky: {
    type: String as PropType<"partial" | "always">,
    default: "partial",
  },
});

const emit = defineEmits(["update:open"]);

const isOpen = ref(false);
const justOpened = ref(false);
const triggerRef = ref();
const portalTarget = ref<HTMLElement>();

watch(isOpen, (val) => {
  emit("update:open", val);

  if (val) {
    const triggerElement = unrefElement(triggerRef);
    const closestPortalTarget = props.portalToClosest
      ? triggerElement?.closest(props.portalToClosest)
      : undefined;
    portalTarget.value =
      closestPortalTarget instanceof HTMLElement
        ? closestPortalTarget
        : undefined;

    justOpened.value = true;
    requestAnimationFrame(() => {
      // justOpened.value = false;
    });
  }
});

let hoverTimeout: NodeJS.Timeout | null = null;
const lastHoverOpenTime = ref(0);

const open = () => {
  if (props.disabled) return;
  isOpen.value = true;
};

const close = () => {
  isOpen.value = false;
};

const showTriggers = computed(() => {
  if (props.showTriggerMethods) {
    return props.showTriggerMethods;
  }

  return props.triggerMethods;
});

const hideTriggers = computed(() => {
  if (props.hideTriggerMethods) {
    return props.hideTriggerMethods;
  }

  return props.triggerMethods;
});

const handleMouseEnter = () => {
  if (mouseLeaveTimeout.value) {
    clearTimeout(mouseLeaveTimeout.value);
    mouseLeaveTimeout.value = null;
  }

  if (props.disabled) return;
  if (showTriggers.value.includes("hover")) {
    hoverTimeout = setTimeout(() => {
      open();
      lastHoverOpenTime.value = Date.now();
    }, 200);
  }
};

const mouseLeaveTimeout = ref<NodeJS.Timeout | null>(null);

const handleMouseLeave = (event: any) => {
  if (hoverTimeout) {
    clearTimeout(hoverTimeout);
    hoverTimeout = null;
  }

  if (hideTriggers.value.includes("hover")) {
    mouseLeaveTimeout.value = setTimeout(() => {
      close();
    }, 50);
  }
};

const handleClick = (event: any) => {
  if (props.disabled) {
    event.preventDefault();
    event.stopPropagation();
    return;
  }

  if (isOpen.value && Date.now() - lastHoverOpenTime.value < 500) {
    event.preventDefault();
    event.stopPropagation();
    return;
  }
};

if (props.controller) {
  props.controller.open = open;
  props.controller.close = close;
}

const outsideEvent = (event: any) => {
  if (props.preventFocusOutside) {
    event.preventDefault();
    return;
  }

  if (useIsOlderSafari() && justOpened.value) {
    event.stopPropagation();
    event.preventDefault();
    justOpened.value = false;
  }
};
</script>

<template>
  <Popover v-model:open="isOpen">
    <PopoverTrigger
      ref="triggerRef"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
      class="form-popover-trigger"
      :class="[triggerClass, { 'cursor-not-allowed': disabled }]"
    >
      <span
        @click="handleClick"
        class="flex items-center justify-center align-middle w-full"
      >
        <slot />
      </span>
    </PopoverTrigger>

    <PopoverContent
      :side="side"
      :align="align"
      :class="contentClass"
      :collision-padding="collisionPadding"
      :sticky="sticky"
      :portal-to="portalTarget"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
      @focus-outside="outsideEvent"
      @open-auto-focus="(e) => e.preventDefault()"
      @close-auto-focus="(e) => e.preventDefault()"
    >
      <div class="rounded bg-surface">
        <slot name="content" />
      </div>
    </PopoverContent>
  </Popover>
</template>

<style scoped></style>
